import { isUnsavedFileType } from 'components/common/graphicUploader/types';
import {
  fileKindService,
  signedRequestService,
} from 'services/fileService';
import {
  GALLERY_FILE_TYPE,
  classifyGalleryFileType,
} from '../../fileKindRoutingModel';
import {
  FilePreviewContext,
  FilePreviewFile,
  FilePreviewResolution,
} from './types';

export const FILE_PREVIEW_LOADING_REASON = {
  VIDEO: 'FileDisplay video loading',
  FILE: 'FileDisplay file.loading',
  IMAGE_SOURCE_PENDING: 'FileDisplay image source pending',
  CONVERTED_PDF: 'FileDisplay converted PDF loading',
} as const;

export function resolveFilePreview(
  file: FilePreviewFile,
  context: FilePreviewContext
): FilePreviewResolution {
  const { storedBlobUrl, drawing } = context;
  const fileType = classifyGalleryFileType(file);

  if (fileType === GALLERY_FILE_TYPE.VIDEO) {
    const videoSource = fileKindService.resolveVideoPreviewSource(file);
    const videoMimeType = fileKindService.resolveVideoMimeType(file);

    if (!videoSource) {
      if (file.loading) {
        return {
          kind: 'loading',
          reason: FILE_PREVIEW_LOADING_REASON.VIDEO,
        };
      }

      return { kind: 'error' };
    }

    return {
      kind: 'video',
      source: videoSource,
      mimeType: videoMimeType,
    };
  }

  if (file.loading) {
    return {
      kind: 'loading',
      reason: FILE_PREVIEW_LOADING_REASON.FILE,
    };
  }

  if (fileType === GALLERY_FILE_TYPE.IMAGE) {
    const imageSource =
      signedRequestService.resolveFullPreviewSource(
        storedBlobUrl,
        drawing,
        file
      ) || file.downloadSrc;

    if (!imageSource) {
      if (isUnsavedFileType(file)) {
        return { kind: 'error' };
      }

      return {
        kind: 'loading',
        reason: FILE_PREVIEW_LOADING_REASON.IMAGE_SOURCE_PENDING,
      };
    }

    return { kind: 'image', source: imageSource };
  }

  if (fileType === GALLERY_FILE_TYPE.CONVERTED_PDF) {
    const fullPreviewSource = signedRequestService.resolveFullPreviewSource(
      storedBlobUrl,
      drawing,
      file
    );
    const thumbnailFallbackSource =
      fileKindService.resolvePdfThumbnailImageSource(file);
    const imageSource = fullPreviewSource ?? thumbnailFallbackSource;
    const pdfSource = fileKindService.resolvePdfPreviewSource(file);

    if (!imageSource && pdfSource) {
      return { kind: GALLERY_FILE_TYPE.PDF, source: pdfSource };
    }

    if (!imageSource) {
      return {
        kind: 'loading',
        reason: FILE_PREVIEW_LOADING_REASON.CONVERTED_PDF,
      };
    }

    return { kind: 'image', source: imageSource };
  }

  if (fileType === GALLERY_FILE_TYPE.PDF) {
    return {
      kind: GALLERY_FILE_TYPE.PDF,
      source: signedRequestService.resolveFileDownloadSource(file),
    };
  }

  if (fileType === GALLERY_FILE_TYPE.WORD) {
    return {
      kind: 'word',
      source: signedRequestService.resolveFileDownloadSource(file),
    };
  }

  if (fileType === GALLERY_FILE_TYPE.EXCEL) {
    return {
      kind: 'excel',
      source: signedRequestService.resolveFileDownloadSource(file),
    };
  }

  return { kind: 'error' };
}
